import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useContext,
  useStore,
  useRoute,
  watch,
} from '@nuxtjs/composition-api'
import { io } from 'socket.io-client'
import { getOrgLkAccess, requestOrgLkRestore } from '@/api/org'
import logger from '@/utils/logger'

const LK_ACCESS_UPDATED_EVENT = 'lk-access:updated'

function getSocketUrl(env) {
  const socketBase = env?.socket_url_dev || ''

  if (socketBase) return socketBase

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }

  return ''
}

/**
 * Проверка доступа к ЛК организации и заявка на восстановление.
 */
export function useLkAccessGuard() {
  const store = useStore()
  const route = useRoute()
  const { env } = useContext()

  const orgId = computed(() => route.value?.params?.orgId)
  const isAdmin = computed(() => {
    const roles = store.state.auth?.user?.roles

    return Array.isArray(roles) && roles.includes('admin')
  })

  const access = ref({
    loading: true,
    enabled: true,
    error: false,
  })
  const restore = ref({
    requested: false,
    loading: false,
    error: false,
  })

  let retryTimerId = null
  let socket = null
  let joinedOrgId = null

  const isRestricted = computed(
    () =>
      !isAdmin.value && !access.value.error && access.value.enabled === false
  )
  const locked = computed(() => isRestricted.value || access.value.error)
  const visible = computed(() => access.value.loading || locked.value)

  function clearRetryTimer() {
    if (!retryTimerId) return

    clearTimeout(retryTimerId)
    retryTimerId = null
  }

  function setRestore(data = {}) {
    restore.value.requested = data?.restoreRequested === true
    clearRetryTimer()

    if (!restore.value.requested || !data?.retryAvailableAt) return

    const delay = new Date(data.retryAvailableAt).getTime() - Date.now()

    if (Number.isNaN(delay) || delay <= 0) {
      restore.value.requested = false
      return
    }

    retryTimerId = setTimeout(() => {
      restore.value.requested = false
      retryTimerId = null
    }, delay)
  }

  function setAccess(payload) {
    if (typeof payload?.enabledLk !== 'boolean') {
      throw new TypeError('Некорректный ответ доступа к ЛК')
    }

    access.value.enabled = payload.enabledLk
    setRestore(payload)
  }

  function joinOrgRoom(id) {
    if (!socket || !id) return

    if (joinedOrgId && String(joinedOrgId) !== String(id)) {
      socket.emit('leave:org', { orgId: joinedOrgId })
    }

    joinedOrgId = id
    socket.emit('join:org', { orgId: id })
  }

  function reloadPage() {
    if (typeof window === 'undefined') return
    window.location.reload()
  }

  function onLkAccessUpdated(payload) {
    if (!payload || String(payload.orgId) !== String(orgId.value)) return

    try {
      if (typeof payload.enabledLk !== 'boolean') {
        throw new TypeError('Некорректный ответ доступа к ЛК')
      }

      if (isRestricted.value && payload.enabledLk === true) {
        reloadPage()
        return
      }

      access.value.error = false
      setAccess(payload)
    } catch (error) {
      logger.error('Ошибка обновления доступа к ЛК:', error)
    }
  }

  function connectSocket() {
    if (typeof window === 'undefined' || socket) return

    const token = store.state.auth?.accessToken || ''

    socket = io(getSocketUrl(env), {
      transports: ['polling', 'websocket'],
      withCredentials: true,
      auth: { token },
    })

    socket.on('connect', () => {
      joinOrgRoom(orgId.value)
    })
    socket.on(LK_ACCESS_UPDATED_EVENT, onLkAccessUpdated)
    socket.on('connect_error', (error) => {
      logger.error(
        'Ошибка socket при подписке на доступ к ЛК:',
        error?.message || error
      )
    })
  }

  function disconnectSocket() {
    if (!socket) return

    if (joinedOrgId) {
      socket.emit('leave:org', { orgId: joinedOrgId })
    }

    socket.off(LK_ACCESS_UPDATED_EVENT, onLkAccessUpdated)
    socket.disconnect()
    socket = null
    joinedOrgId = null
  }

  async function load() {
    access.value.error = false

    if (isAdmin.value || !orgId.value) {
      access.value.enabled = true
      access.value.loading = false
      setRestore()
      return
    }

    access.value.loading = true

    try {
      const response = await getOrgLkAccess(orgId.value)
      setAccess(response?.data)
    } catch (error) {
      logger.error('Ошибка проверки доступа к ЛК:', error)
      access.value.error = true
    } finally {
      access.value.loading = false
    }
  }

  async function requestRestore() {
    if (!orgId.value || restore.value.loading || restore.value.requested) {
      return
    }

    restore.value.loading = true
    restore.value.error = false

    try {
      const response = await requestOrgLkRestore(orgId.value)
      setRestore(response?.data)
    } catch (error) {
      logger.error('Ошибка заявки на восстановление доступа к ЛК:', error)
      restore.value.error = true
    } finally {
      restore.value.loading = false
    }
  }

  watch(
    orgId,
    (id) => {
      load()
      joinOrgRoom(id)
    },
    { immediate: true }
  )

  onMounted(() => {
    connectSocket()
  })

  onBeforeUnmount(() => {
    clearRetryTimer()
    disconnectSocket()
  })

  return {
    visible,
    locked,
    restore,
    access,
    requestRestore,
  }
}
